import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, fmtKg, batimentColor } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, ScanLine } from "lucide-react";
import ScanPalette, { parsePaletteCode, feedback } from "@/components/ScanPalette";

export default function NouveauBL() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const today = new Date().toISOString().slice(0, 10);
  const [recipients, setRecipients] = useState([]);
  const [recId, setRecId] = useState("");
  const [palettes, setPalettes] = useState([]);
  const [selected, setSelected] = useState({});
  const [meta, setMeta] = useState({ transporteur: "", num_vehicule: "", date_depart: today });
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const lastCodeRef = useRef({ code: "", at: 0 });
  const recipientsRef = useRef([]);
  const recIdRef = useRef("");

  useEffect(() => {
    api.get("/recipients").then((r) => { setRecipients(r.data); recipientsRef.current = r.data; });
  }, []);
  useEffect(() => { recIdRef.current = recId; }, [recId]);

  const recipient = recipients.find((r) => r.id === recId);
  const blType = recipient?.type_accepte;

  useEffect(() => {
    if (!blType) {
      setPalettes([]);
      return;
    }
    api.get("/palettes", { params: { type: blType, statut: "en_stock" } }).then((r) => {
      setPalettes(r.data);
      setSelected((s) => Object.fromEntries(Object.entries(s).filter(([id]) => r.data.some((p) => p.id === id))));
    });
  }, [blType]);

  const report = (ok, message) => {
    feedback(ok);
    setLastScan({ ok, message });
    (ok ? toast.success : toast.error)(message);
  };

  const handleCode = useCallback(async (raw) => {
    const code = parsePaletteCode(raw);
    if (!code) return report(false, `Code non reconnu : ${String(raw).slice(0, 30)}`);
    const now = Date.now();
    if (lastCodeRef.current.code === code && now - lastCodeRef.current.at < 2500) return;
    lastCodeRef.current = { code, at: now };
    try {
      const { data: p } = await api.get(`/palettes/by-code/${code}`);
      if (p.statut !== "en_stock")
        return report(false, `${code} déjà expédiée${p.bl_numero ? ` (${p.bl_numero})` : ""}`);
      let rec = recipientsRef.current.find((r) => r.id === recIdRef.current);
      if (!rec) {
        rec = recipientsRef.current.find((r) => r.type_accepte === p.type);
        if (!rec) return report(false, `Aucun destinataire n'accepte les palettes ${p.type === "P" ? "conformes" : "déclassées"}`);
        setRecId(rec.id);
        recIdRef.current = rec.id;
      } else if (rec.type_accepte !== p.type) {
        return report(false, `${code} est ${p.type === "P" ? "conforme" : "déclassée"} — incompatible avec ${rec.nom} (mélange interdit)`);
      }
      setPalettes((list) => (list.some((x) => x.id === p.id) ? list : [p, ...list]));
      setSelected((s) => ({ ...s, [p.id]: true }));
      report(true, `${code} ajoutée au BL — ${p.batiment} · ${fmtKg(p.poids_net)}`);
    } catch (err) {
      report(false, apiError(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    const code = params.get("code");
    if (code && recipients.length) handleCode(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipients.length]);

  const chosen = palettes.filter((p) => selected[p.id]);
  const totalNet = chosen.reduce((s, p) => s + p.poids_net, 0);
  const totalBrut = chosen.reduce((s, p) => s + p.poids_brut, 0);

  const submit = async () => {
    if (!recId) return toast.error("Sélectionnez un destinataire.");
    if (chosen.length === 0) return toast.error("Sélectionnez au moins une palette.");
    try {
      const res = await api.post("/bl", {
        destinataire_id: recId,
        palette_ids: chosen.map((p) => p.id),
        transporteur: meta.transporteur,
        num_vehicule: meta.num_vehicule,
        date_depart: meta.date_depart,
      });
      toast.success(`Bon de livraison ${res.data.numero} généré`);
      navigate(`/bl/${res.data.id}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="nouveau-bl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading font-black text-3xl uppercase tracking-tight text-slate-900">
          Nouveau bon de livraison
        </h1>
        {!scanning && (
          <Button data-testid="scan-start-btn" onClick={() => { setLastScan(null); setScanning(true); }}
            className="h-14 px-6 bg-slate-900 hover:bg-slate-800 font-bold text-base">
            <ScanLine className="w-6 h-6 mr-2" /> Scanner des palettes
          </Button>
        )}
      </div>

      {scanning && (
        <ScanPalette onCode={handleCode} onClose={() => setScanning(false)} lastResult={lastScan}
          count={chosen.length} countLabel="palettes sur le BL" />
      )}

      <div className="rounded-2xl bg-white border-2 border-slate-200 p-5 grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Destinataire</Label>
          <Select value={recId} onValueChange={setRecId}>
            <SelectTrigger data-testid="bl-client-select" className="mt-1 h-12">
              <SelectValue placeholder="Choisir un destinataire (ou scannez une palette)" />
            </SelectTrigger>
            <SelectContent>
              {recipients.map((r) => (
                <SelectItem key={r.id} value={r.id} data-testid={`bl-client-option-${r.nom}`}>
                  {r.nom} — {r.type_accepte === "P" ? "Conforme (P)" : "Déclassé"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {recipient && (
            <div className={`mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-md text-xs font-bold uppercase ${
              blType === "P" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"
            }`}>
              BL {blType === "P" ? "Conforme (P)" : "Déclassé"} — {recipient.adresse}, {recipient.cp_ville}
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Transporteur</Label>
          <Input data-testid="bl-transporteur" value={meta.transporteur}
            onChange={(e) => setMeta({ ...meta, transporteur: e.target.value })} className="mt-1 h-12" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">N° véhicule</Label>
          <Input data-testid="bl-vehicule" value={meta.num_vehicule}
            onChange={(e) => setMeta({ ...meta, num_vehicule: e.target.value })} className="mt-1 h-12" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Date de départ</Label>
          <Input data-testid="bl-date-depart" type="date" value={meta.date_depart}
            onChange={(e) => setMeta({ ...meta, date_depart: e.target.value })} className="mt-1 h-12" />
        </div>
      </div>

      {recId && (
        <div className="rounded-2xl bg-white border-2 border-slate-200 p-5">
          <h2 className="font-heading font-bold text-xl uppercase tracking-tight text-slate-900 mb-3">
            Palettes en stock ({palettes.length}) — {chosen.length} sélectionnée{chosen.length > 1 ? "s" : ""}
          </h2>
          {palettes.length === 0 ? (
            <p className="text-slate-400 font-medium py-6 text-center">
              Aucune palette {blType === "P" ? "conforme" : "déclassée"} en stock.
            </p>
          ) : (
            <div className="space-y-2">
              {palettes.map((p) => (
                <label
                  key={p.id}
                  data-testid={`bl-palette-${p.code_qr}`}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                    selected[p.id] ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <Checkbox
                    checked={!!selected[p.id]}
                    onCheckedChange={(v) => setSelected((s) => ({ ...s, [p.id]: v }))}
                  />
                  <span className="font-mono font-bold text-slate-900">{p.code_qr}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${batimentColor(p.batiment)}`}>{p.batiment}</span>
                  <span className="text-slate-500 text-sm">{fmtDate(p.date_ponte)}</span>
                  {p.motif && <span className="text-rose-700 text-xs font-bold">{p.motif}</span>}
                  <span className="ml-auto font-mono font-bold">{fmtKg(p.poids_net)}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {chosen.length > 0 && (
        <div className="rounded-2xl bg-slate-900 text-white p-5 flex flex-wrap items-center justify-between gap-4 sticky bottom-4">
          <div className="flex gap-6 font-mono">
            <div><div className="text-[10px] uppercase text-slate-400 font-bold">Palettes</div><div data-testid="bl-selected-count" className="font-black text-2xl">{chosen.length}</div></div>
            <div><div className="text-[10px] uppercase text-slate-400 font-bold">Brut total</div><div className="font-black text-2xl">{totalBrut.toFixed(1)} kg</div></div>
            <div><div className="text-[10px] uppercase text-slate-400 font-bold">Net total</div><div className="font-black text-2xl text-emerald-400">{totalNet.toFixed(1)} kg</div></div>
          </div>
          <Button data-testid="bl-generate-submit-btn" onClick={submit}
            className="h-14 bg-emerald-600 hover:bg-emerald-700 font-bold text-base px-8">
            <FileText className="w-5 h-5 mr-2" /> Générer & valider le BL
          </Button>
        </div>
      )}
    </div>
  );
}
